import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { setActiveTab, fetchReviews } from '../../store/slices/googleReviewSlice';

type Tab = 'all' | 'needs_reply' | 'draft' | 'published';

const ReviewTabBar: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const {
    counts,
    activeTab,
    selectedLocationId,
    ratingFilter,
    includeRatingOnly,
  } = useAppSelector((s) => s.googleReview);

  // Counts come from the server over the full result set, so they stay correct regardless of
  // which tab is active or how the list is paged.
  const countFor: Record<Tab, number> = {
    all: counts.all,
    needs_reply: counts.needsReply,
    draft: counts.draft,
    published: counts.published,
  };

  const handleTab = (tab: Tab) => {
    dispatch(setActiveTab(tab));
    if (selectedLocationId) {
      // Status filtering is server-side now - refetch the tab's slice.
      dispatch(fetchReviews({ locationId: selectedLocationId, filter: tab, rating: ratingFilter, includeRatingOnly }));
    }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'all', label: t('google_review_tab_all') },
    { id: 'needs_reply', label: t('google_review_tab_needs_reply') },
    { id: 'draft', label: t('google_review_tab_draft') },
    { id: 'published', label: t('google_review_tab_published') },
  ];

  return (
    <div className="review-tab-bar" role="tablist">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          role="tab"
          aria-selected={activeTab === tab.id}
          className={`review-tab-bar__tab${activeTab === tab.id ? ' review-tab-bar__tab--active' : ''}`}
          onClick={() => handleTab(tab.id)}
        >
          {tab.label}
          <span className="review-tab-bar__count">{countFor[tab.id]}</span>
        </button>
      ))}
    </div>
  );
};

export default ReviewTabBar;
